import { Session } from '@aspendental/shared-components-web';
import '@aspendental/shared-components-web/lib/fonts.css';
import { getSessionId } from '@aspendental/shared-utils/utils';
import '@contentful/live-preview/style.css';
import { Faro, FaroErrorBoundary } from '@grafana/faro-react';
import * as Sentry from '@sentry/nextjs';
import { ContextProviders } from '@src/components/shared/providers/ContextProviders';
import { Layout } from '@src/components/templates/layout';
import { getLogger } from '@src/lib/logging/logger';
import { initGrafanaFaro } from '@src/services/faro';
import { isSentryEnabled, toggleSentry } from '@src/services/sentry';
import { useAppStore, useStepManagerStore } from '@src/store';
import { checkGADataExpiry } from '@src/utils/check-GA-data-expiry';
import { storeGADataToLocalStorage } from '@src/utils/store-GA-data-to-local-storage';
import { storeGRDataCookie } from '@src/utils/store-GR-data-to-cookies';
import { NextPage } from 'next';
import { appWithTranslation } from 'next-i18next';
import type { AppProps, NextWebVitalsMetric } from 'next/app';
import { useRouter } from 'next/router';
import { ReactElement, ReactNode, useEffect, useRef } from 'react';

// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
import { BrandThemeConfig } from 'types/tag';
export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};
type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};
export function reportWebVitals({
  id,
  name,
  label,
  value
}: NextWebVitalsMetric) {
  const logger = getLogger('client');
  logger.info({
    name,
    category: label === 'web-vital' ? 'Web Vitals' : 'Next.js custom metric',
    value: Math.round(name === 'CLS' ? value * 1000 : value),
    // values must be integers
    id // id unique to current page load
  });
}
declare global {
  interface Window {
    ga: any;
    LogRocket: any;
  }
}
const App = ({
  Component,
  pageProps
}: AppPropsWithLayout) => {
  toggleSentry(!!pageProps?.config?.features?.sentry?.enabled);
  const router = useRouter();
  const config = pageProps?.config as BrandThemeConfig;
  const faroRef = useRef<null | Faro>(null);
  const {
    setReferrer
  } = useAppStore();
  useEffect(() => {
    let ref = document.referrer;
    if (ref.includes('://localhost')) {
      ref = ref.replace('://localhost', '://local.dev');
    }
    setReferrer(ref);
  }, []);
  useEffect(() => {
    if (!faroRef.current && config?.features?.realUserMonitoring?.enabled) {
      faroRef.current = initGrafanaFaro(config.features.realUserMonitoring.faroApiKey);
    }
  }, [config?.features?.realUserMonitoring]);
  const brandName = config?.name || 'ClearChoice Dental Implants';
  useEffect(() => {
    if (isSentryEnabled()) {
      Sentry.setTag('brand', brandName);
      Sentry.setContext('user', {
        sessionId: getSessionId(),
        brand: brandName
      });
    }
  }, [brandName]);
  useEffect(() => {
    if (pageProps.appData.featureFlags.enablePersistOnClearChoiceScheduler) {
      useStepManagerStore.getState().restoreStepOnLoad();
    } else {
      useStepManagerStore.getState().clear();
    }
    checkGADataExpiry();
    storeGADataToLocalStorage();
    storeGRDataCookie();
  }, [pageProps.appData.featureFlags.enablePersistOnClearChoiceScheduler]);
  if (router.pathname === '_error') return <Component {...pageProps} />;
  const getLayout = Component.getLayout ?? (page => {
    return <Layout preview={pageProps.preview} appData={pageProps.appData} isFooterLite={config?.features?.footer?.lite} withSimpleLayout={config?.features?.footer?.withSimpleLayout} footerData={pageProps.footerData} logoHref={pageProps.env?.ENVIRONMENT_NAME === 'prod' ? config?.features?.livePreview?.prod : config?.features?.livePreview?.stage} smartyKey={pageProps.env.TAG_SMARTY_EMBEDDED_KEY} successCards={pageProps?.successCardsCollection?.items}>
					{page}
				</Layout>;
  });
  return <FaroErrorBoundary data-sentry-element="FaroErrorBoundary" data-sentry-component="App" data-sentry-source-file="_app.page.tsx">
			<Session data-sentry-element="Session" data-sentry-source-file="_app.page.tsx">
				<ContextProviders config={config} preview={pageProps.preview} environment={pageProps.env?.ENVIRONMENT_NAME} user={pageProps.user} defaultToCdn={pageProps?.appData.featureFlags} data-sentry-element="ContextProviders" data-sentry-source-file="_app.page.tsx">
					{getLayout(<Component {...pageProps} />)}
				</ContextProviders>
			</Session>
		</FaroErrorBoundary>;
};
export default appWithTranslation(App);